import { AfterViewInit, Component, ElementRef, inject, Injector, OnDestroy, OnInit, ViewChild, signal, input, output } from '@angular/core'
import { form } from '@angular/forms/signals'
import { YtjYrityksenTiedotPyynto, YtjYrityksenTiedotVastaus, YtjYrityshakuPyynto, YtjYrityshakuVastaus, YtjYrityshaunTulos } from '@lemon/core-firebase/model/ytj-api'
import { CodeCheckService } from '@lemon/core/service/code-check.service'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators'
import { FirebaseLemonaid } from '../../_angular/service/firebase-lemonaid.service'
import { arvonMuutokset, asetaArvo } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'

interface YtjHakutulosNakymassa {
  yritys: YtjYrityshaunTulos
  nimiEnnenOsumaa: string
  nimenOsuma: string
  nimiOsumanJalkeen: string
  osoiteteksti: string | null
}

type YtjHaunTila = 'odottaa' | 'haetaan' | 'haetaan-osoitetta' | 'valmis' | 'haku-epaonnistui' | 'osoitehaku-epaonnistui'

const YTJ_HAKUSANAN_VAHIMMAISPITUUS = 3

@Component({
  selector: 'app-ytj-haku',
  templateUrl: './ytj-haku.component.html',
  styleUrls: ['./ytj-haku.component.css'],
  standalone: false,
})
export class YtjHakuComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('ytjHakukentta') ytjHakukentta: ElementRef<HTMLInputElement>

  readonly esitaytettyHakusana = input<string>()
  readonly valittuYtunnus = input<string>()
  readonly yritysValittu = output<YtjYrityshaunTulos>()
  readonly osoiteSaatu = output<YtjYrityshaunTulos>()

  readonly ytjHakusana = form(signal(''))
  readonly ytjTulokset = signal<YtjHakutulosNakymassa[] | null>(null)
  readonly ytjHaunTila = signal<YtjHaunTila>('odottaa')
  readonly ytjListaAuki = signal(false)
  readonly korostettuYtjIndeksi = signal(0)

  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  constructor(
    private _codeCheckService: CodeCheckService,
    private _firebaseLemonaid: FirebaseLemonaid,
  ) {}

  ngOnInit() {
    arvonMuutokset(this.ytjHakusana, { injector: this._injector })
      .pipe(
        debounceTime(500),
        map(value => value?.trim() ?? ''),
        distinctUntilChanged(),
        switchMap(hakusana => this._suoritaYtjYrityshaku(hakusana)),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(tulokset => this._naytaYtjTulokset(tulokset))

    asetaArvo(this.ytjHakusana, this.esitaytettyHakusana() ?? '')
  }

  ngAfterViewInit() {
    setTimeout(() => this.ytjHakukentta?.nativeElement.focus())
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  async valitseYtjYritys(tulos: YtjHakutulosNakymassa) {
    const yritys = tulos.yritys

    this._suljeYtjHakulista(yritys.nimi)
    this.yritysValittu.emit(yritys)

    if (this._onkoOsoiteMukana(yritys)) {
      this.osoiteSaatu.emit(yritys)
      this.ytjHaunTila.set('valmis')
      return
    }

    this.ytjHaunTila.set('haetaan-osoitetta')
    try {
      const pyynto: YtjYrityksenTiedotPyynto = { ytunnus: yritys.ytunnus }
      const vastaus = await this._firebaseLemonaid.functionsCall<YtjYrityksenTiedotPyynto, YtjYrityksenTiedotVastaus>('laskutYtjTiedot', pyynto, undefined, 'europe-north1')
      const onkoYritysYhaValittuna = this.valittuYtunnus() === yritys.ytunnus
      if (!onkoYritysYhaValittuna) {
        this.ytjHaunTila.set('odottaa')
        return
      }
      if (!vastaus?.yritys) {
        this.ytjHaunTila.set('osoitehaku-epaonnistui')
        return
      }
      const osoitteellinenYritys: YtjYrityshaunTulos = { ...vastaus.yritys, ytunnus: yritys.ytunnus }
      this.osoiteSaatu.emit(osoitteellinenYritys)
      this.ytjHaunTila.set('valmis')
    } catch (err) {
      console.error('YTJ-tietojen haku epäonnistui', err)
      const onkoYritysYhaValittuna = this.valittuYtunnus() === yritys.ytunnus
      this.ytjHaunTila.set(onkoYritysYhaValittuna ? 'osoitehaku-epaonnistui' : 'odottaa')
    }
  }

  private _suljeYtjHakulista(valitunYrityksenNimi: string) {
    asetaArvo(this.ytjHakusana, valitunYrityksenNimi ?? '', { ilmoita: false })
    this.ytjTulokset.set(null)
    this.ytjListaAuki.set(false)
  }

  private _onkoOsoiteMukana(yritys: YtjYrityshaunTulos): boolean {
    return !!yritys.katuosoite || !!yritys.postinro || !!yritys.postitmp
  }

  kasitteleYtjHakukentanNappain(event: KeyboardEvent) {
    const tulokset = this.ytjTulokset()
    if (!tulokset?.length) {
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.korostettuYtjIndeksi.set(Math.min(tulokset.length - 1, this.korostettuYtjIndeksi() + 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      this.korostettuYtjIndeksi.set(Math.max(0, this.korostettuYtjIndeksi() - 1))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      this.valitseYtjYritys(tulokset[this.korostettuYtjIndeksi()])
    } else if (event.key === 'Escape') {
      this.ytjListaAuki.set(false)
    }
  }

  suljeYtjListaViiveella() {
    setTimeout(() => {
      this.ytjListaAuki.set(false)
    }, 150)
  }

  private async _suoritaYtjYrityshaku(hakusana: string): Promise<YtjYrityshakuVastaus | null> {
    this.ytjHaunTila.set('odottaa')
    const onkoYtunnus = this._codeCheckService.isValidYTunnus(hakusana)
    const onkoHakusanaRiittava = hakusana.length >= YTJ_HAKUSANAN_VAHIMMAISPITUUS
    if (!onkoYtunnus && !onkoHakusanaRiittava) {
      return null
    }
    this.ytjHaunTila.set('haetaan')

    try {
      const pyynto: YtjYrityshakuPyynto = { hakusana }
      return await this._firebaseLemonaid.functionsCall<YtjYrityshakuPyynto, YtjYrityshakuVastaus>('laskutYtjHaku', pyynto, undefined, 'europe-north1')
    } catch (err) {
      console.error('YTJ-haku epäonnistui', err)
      const virhevastaus: YtjYrityshakuVastaus = { yritykset: [], e: 'virhe' }
      return virhevastaus
    }
  }

  private _naytaYtjTulokset(vastaus: YtjYrityshakuVastaus | null) {
    this.ytjHaunTila.set(vastaus?.e ? 'haku-epaonnistui' : 'odottaa')
    this.korostettuYtjIndeksi.set(0)
    this.ytjListaAuki.set(true)
    if (!vastaus || vastaus.e) {
      this.ytjTulokset.set(null)
      return
    }
    const hakusana = this.ytjHakusana().value()?.trim().toLowerCase() ?? ''
    this.ytjTulokset.set(vastaus.yritykset.map(yritys => this._annaYtjHakutulosNakymassa(yritys, hakusana)))
  }

  private _annaYtjHakutulosNakymassa(yritys: YtjYrityshaunTulos, hakusana: string): YtjHakutulosNakymassa {
    const osoiteteksti = this._onkoOsoiteMukana(yritys) ? `${yritys.katuosoite ?? ''}, ${yritys.postinro ?? ''} ${yritys.postitmp ?? ''}`.trim() : null
    const osumanAlku = hakusana ? yritys.nimi.toLowerCase().indexOf(hakusana) : -1
    if (osumanAlku < 0) {
      return { yritys, nimiEnnenOsumaa: yritys.nimi, nimenOsuma: '', nimiOsumanJalkeen: '', osoiteteksti }
    }
    const osumanLoppu = osumanAlku + hakusana.length
    return {
      yritys,
      nimiEnnenOsumaa: yritys.nimi.slice(0, osumanAlku),
      nimenOsuma: yritys.nimi.slice(osumanAlku, osumanLoppu),
      nimiOsumanJalkeen: yritys.nimi.slice(osumanLoppu),
      osoiteteksti,
    }
  }
}
